/**
 * 采集批次列表页面
 * 展示用户所有素材包（采集批次）的列表
 * 支持平台和状态筛选、分页浏览
 */

import type { ReactElement } from 'react'
import { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Inbox,
  ChevronLeft,
  ChevronRight,
  Package,
  Filter,
  Search,
  X,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useCrawlBatches, useDebounce } from '@/hooks'
import { CrawlBatchCard } from '@/components/CrawlBatchCard'
import {
  CRAWL_BATCH_PLATFORM,
} from '@/types/crawlBatch'
import type { CrawlBatchPlatform } from '@/types/crawlBatch'

/**
 * 分页组件属性
 */
interface PaginationProps {
  /** 当前页码 */
  currentPage: number
  /** 总页数 */
  totalPages: number
  /** 页码变化回调 */
  onPageChange: (page: number) => void
}

/**
 * 分页组件
 */
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps): ReactElement {
  if (totalPages <= 1) {
    return <></>
  }

  /**
   * 生成页码数组
   */
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1)

      if (currentPage > 3) {
        pages.push('...')
      }

      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (currentPage < totalPages - 2) {
        pages.push('...')
      }

      pages.push(totalPages)
    }

    return pages
  }

  return (
    <div className="flex items-center justify-center gap-1">
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8"
        onClick={(): void => {
          onPageChange(currentPage - 1)
        }}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {getPageNumbers().map((page, index) =>
        typeof page === 'number' ? (
          <Button
            key={page}
            variant={page === currentPage ? 'default' : 'outline'}
            size="icon"
            className="h-8 w-8"
            onClick={(): void => {
              onPageChange(page)
            }}
          >
            {page}
          </Button>
        ) : (
          <span
            key={`ellipsis-${String(index)}`}
            className="px-2 text-muted-foreground"
          >
            ...
          </span>
        )
      )}

      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8"
        onClick={(): void => {
          onPageChange(currentPage + 1)
        }}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}

/**
 * 加载骨架屏组件 - 网格卡片模式
 */
function LoadingSkeleton(): ReactElement {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 12 }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse rounded-xl border bg-card p-4"
        >
          {/* 图标骨架 */}
          <div className="mb-3 h-10 w-10 rounded-lg bg-muted" />
          {/* 标题骨架 */}
          <div className="space-y-3">
            <div className="h-4 w-4/5 rounded bg-muted" />
            <div className="space-y-1.5">
              <div className="h-3 w-24 rounded bg-muted" />
              <div className="h-3 w-20 rounded bg-muted" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * 空状态组件
 */
function EmptyState(): ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center rounded-2xl border bg-card py-16"
    >
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
        <Inbox className="h-10 w-10 text-muted-foreground/50" />
      </div>
      <p className="text-lg font-medium text-muted-foreground">暂无素材包</p>
      <p className="mt-1 text-sm text-muted-foreground/70">
        尚未采集任何商品素材
      </p>
    </motion.div>
  )
}

/**
 * 筛选无结果组件
 */
interface NoResultsProps {
  onClear: () => void
}

function NoResults({ onClear }: NoResultsProps): ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center rounded-2xl border bg-card py-16"
    >
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <Filter className="h-8 w-8 text-muted-foreground/50" />
      </div>
      <p className="text-lg font-medium text-muted-foreground">
        未找到匹配的素材包
      </p>
      <p className="mt-1 text-sm text-muted-foreground/70">
        试试清空筛选条件
      </p>
      <Button variant="outline" className="mt-6" onClick={onClear}>
        清空筛选
      </Button>
    </motion.div>
  )
}

/**
 * 采集批次列表页面组件
 * @returns 页面 ReactElement
 */
export function CrawlBatchesPage(): ReactElement {
  // 本地搜索输入状态（即时响应）
  const [searchInput, setSearchInput] = useState('')
  // 防抖后的搜索值
  const debouncedSearch = useDebounce(searchInput, 300)

  // 使用自定义 hook 获取数据
  const {
    items,
    isLoading,
    error,
    total,
    page,
    pageSize,
    platform,
    remark,
    setPage,
    setPlatform,
    setRemark,
    refresh,
  } = useCrawlBatches(20)

  // 计算总页数
  const totalPages = Math.ceil(total / pageSize)

  // 判断是否正在防抖中（输入值与实际搜索值不同）
  const isDebouncing = searchInput !== remark

  /**
   * 同步防抖后的搜索值到 hook
   */
  useEffect(() => {
    if (debouncedSearch !== remark) {
      setRemark(debouncedSearch)
      setPage(1) // 搜索变化时重置到第 1 页
    }
  }, [debouncedSearch, remark, setRemark, setPage])

  /**
   * 清空筛选
   */
  const handleClearFilters = useCallback((): void => {
    setPlatform('')
    setSearchInput('')
    setRemark('')
  }, [setPlatform, setRemark])

  /**
   * 清空搜索输入
   */
  const handleClearSearch = useCallback((): void => {
    setSearchInput('')
  }, [])

  // 判断是否有任何筛选条件
  const hasFilters = platform !== '' || searchInput !== ''

  return (
    <div className="space-y-6">
      {/* 页面头部 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Package className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">我的素材包</h1>
          </div>
          <p className="text-muted-foreground">
            管理您的采集的数据，查看的商品素材。
          </p>
        </div>
      </div>

      {/* 筛选表单 */}
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>筛选</span>
        </div>

        {/* 搜索输入框 */}
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(e): void => {
              setSearchInput(e.target.value)
            }}
            placeholder="搜索备注..."
            className={cn(
              'h-9 w-full rounded-md border border-input bg-background pl-9 pr-9 text-sm outline-none',
              'focus:border-primary focus:ring-1 focus:ring-primary transition-all',
              'placeholder:text-muted-foreground'
            )}
          />
          {/* 右侧：清空按钮或加载指示器 */}
          {searchInput !== '' && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {isDebouncing ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* 平台筛选 */}
        <select
          value={platform}
          onChange={(event): void => {
            setPlatform(event.target.value as CrawlBatchPlatform | '')
          }}
          className={cn(
            'h-9 rounded-md border border-input bg-background px-3 text-sm outline-none',
            'focus:border-primary focus:ring-1 focus:ring-primary transition-all',
            'sm:w-36'
          )}
        >
          <option value="">全部平台</option>
          <option value={CRAWL_BATCH_PLATFORM.TEMU}>Temu</option>
        </select>

        {/* 清空筛选按钮 */}
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="text-muted-foreground"
          >
            清空筛选
          </Button>
        )}
      </div>

      {/* 错误提示 */}
      <AnimatePresence>
        {error !== null && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-600 dark:text-red-400"
          >
            <p>{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={(): void => {
                void refresh()
              }}
            >
              重试
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 主内容区 */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : items.length === 0 ? (
        hasFilters ? (
          <NoResults onClear={handleClearFilters} />
        ) : (
          <EmptyState />
        )
      ) : (
        <>
          {/* 豆腐块网格布局 */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((batch) => (
              <CrawlBatchCard
                key={batch.id}
                batch={batch}
                onRefresh={(): void => {
                  void refresh()
                }}
              />
            ))}
          </div>

          {/* 分页信息和控件 */}
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
            <p className="text-sm text-muted-foreground">
              共 {total} 条记录，第 {page}/{totalPages} 页
            </p>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
    </div>
  )
}
